import SocialSection from '../components/SocialSection';
import ContactUsButton from '../components/ContactUsButton';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { freebordSpotlightItems } from '../data/freebord';
import FallbackImage from '../components/FallbackImage';

const HomePage = () => {
  const { i18n } = useTranslation();
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BlueBoard",
    "url": "https://blueboard.bloodshop.org",
    "description": "Premium electric skateboards and personal transportation solutions in Israel",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://blueboard.bloodshop.org/products?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <SEO
        title="BlueBoard - Premium Electric Skateboards & Personal Transportation | Israel"
        description="Discover premium electric skateboards and personal transportation solutions in Israel."
        keywords="electric skateboard, personal transportation, FREEBORD, Israel, Tel Aviv, Jerusalem, Haifa, electric vehicle, e-skate, mobility"
        url="/"
        type="website"
        lang={i18n.language as 'en' | 'he'}
        structuredData={structuredData}
      />
      <div className="relative min-h-screen bg-black">
      <div className="fixed top-0 left-0 w-full h-full z-0">
        <video
          className="w-full h-full object-cover"
          src="/vidoes/31 - mezzanine.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50"></div>
      </div>

      <main className="relative z-10">
        <HeroSection />

        <div className="h-40 bg-transparent"></div>

        <PricingSection />

        <div className="h-40 bg-transparent"></div>

        <div className="px-4 md:px-8 lg:px-16">
          <VibeSection />
        </div>

        <div className="h-40 bg-transparent"></div>

        <div className="px-4 md:px-8 lg:px-16">
          <ComplementaryProductsSection />
        </div>

        <div className="h-40 bg-transparent"></div>

        <div className="px-4 md:px-8 lg:px-16">
          <FreebordNewArrivalsSection />
        </div>

        <div className="h-40 bg-transparent"></div>

        {/* Feature Logos Section */}
        <div className="px-4 md:px-8 lg:px-16 mt-8" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
          <FeatureLogosSection />
        </div>

        {/* Google Maps Section */}
        <div className="px-4 md:px-8 lg:px-16 mb-12" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
          <GoogleMapSection />
        </div>

        <div className="h-40 bg-transparent"></div>

        {/* Social Section - minimalized version */}
        <div className="px-4 md:px-8 lg:px-16" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
          <SocialSection />
        </div>
      </main>
    </div>
    </>
  );
};

const HeroSection = () => {
  const { t } = useTranslation();
  return (
  <div className="container mx-auto px-4 text-center text-white min-h-screen flex flex-col justify-center">
    <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-4">BLUE BOARD</h1>
      <p className="text-xl md:text-2xl text-gray-300 mb-6">{t('hero.title')}</p>
      <ContactUsButton className="mt-2 mx-auto px-8 py-4 text-lg md:text-xl font-bold shadow-lg">
        {t('contact.testRideButton')}
      </ContactUsButton>
  </div>
);
};

const PricingSection = () => {
  const { t } = useTranslation();
  return (
  <section className="bg-white bg-opacity-90 py-12 text-center rounded-lg mx-4 md:mx-8 lg:mx-16">
      <h2 className="text-5xl font-bold text-gray-800">{t('home.pricingTitle')}</h2>
      <p className="text-3xl text-gray-600 mt-4">{t('home.pricingSubtitle')}</p>
  </section>
);
};

const VibeSection = () => (
  <section className="bg-gray-100 py-10 rounded-lg">
    <div className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
      <div className="w-full h-96">
        <img 
          src="/pictures/1-DSC00455.jpg"
          alt="Vibe 2" 
          className="w-full h-full object-cover rounded-lg shadow-lg"
        />
      </div>
      <div className="w-full h-96">
        <video 
          src="/vidoes/vibe3.mp4"
          className="w-full h-full object-cover rounded-lg shadow-lg"
          autoPlay
          loop
          muted
          playsInline
        />
      </div>
      <div className="w-full h-96">
        <img 
          src="/pictures/10-DSC00353.jpg"
          alt="Vibe 1"
          className="w-full h-full object-cover rounded-lg shadow-lg"
        />
      </div>
    </div>
  </section>
);

const ComplementaryProductsSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const products = [
    {
      id: 4, // Remote Control
      imgSrc: "/pictures/remote_control.avif",
      title: t('home.complementary.remote'),
    },
    {
      id: 2, // T Key
      imgSrc: "/pictures/t_tool.avif",
      title: t('home.complementary.key'),
    },
    {
      id: 5, // 36V Charger
      imgSrc: "/pictures/charger.avif",
      title: t('home.complementary.charger'),
    },
    {
      id: 1, // Lamps
      imgSrc: "/pictures/4_lamps.avif",
      title: t('home.complementary.lamps'),
    },
    {
      id: null, // Multi-charger - not in products yet
      imgSrc: "/pictures/4_in_1_cable.avif",
      title: t('home.complementary.multiCharger'),
    }
  ];

  const handleProductClick = (productId: number | null) => {
    if (productId) {
      navigate(`/products/${productId}`);
    }
  };

  return (
    <section className="bg-white py-20 rounded-lg">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">{t('home.complementary.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
          {products.map((product, index) => (
            <div 
              key={index} 
              className={`bg-gray-50 rounded-lg shadow-lg overflow-hidden ${product.id ? 'cursor-pointer hover:shadow-xl transition-shadow duration-300' : ''}`}
              onClick={() => handleProductClick(product.id)}
            >
              <img src={product.imgSrc} alt={product.title} className="w-full h-80 object-cover"/>
              <div className="p-6 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{product.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FreebordNewArrivalsSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language as 'en' | 'he';

  return (
    <section className="rounded-[2rem] border border-cyan-200/30 bg-[linear-gradient(135deg,_rgba(255,255,255,0.94)_0%,_rgba(224,242,254,0.96)_50%,_rgba(207,250,254,0.94)_100%)] px-6 py-16 shadow-2xl shadow-slate-900/10 md:px-10">
      <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-700">
            {t('home.freebordNewArrivals.eyebrow')}
          </p>
          <h2 className="mt-3 text-4xl font-black uppercase tracking-tight text-slate-900">
            {t('home.freebordNewArrivals.title')}
          </h2>
        </div>
        <p className="max-w-2xl text-base leading-7 text-slate-700">
          {t('home.freebordNewArrivals.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {freebordSpotlightItems.map((item) => (
          <div key={item.id} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-900/5">
            <div className="flex aspect-square items-center justify-center rounded-2xl bg-slate-50 p-4">
              <FallbackImage
                srcs={item.imageSources}
                alt={item.title[currentLang]}
                className="h-full w-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="mt-5 flex items-center justify-between gap-4">
              <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-cyan-200">
                {item.accent}
              </span>
              <span className="text-sm font-semibold text-slate-600">₪ {item.nisPrice.toLocaleString()}</span>
            </div>
            <h3 className="mt-8 text-2xl font-black uppercase text-slate-900">{item.title[currentLang]}</h3>
            <p className="mt-4 min-h-[88px] text-sm leading-7 text-slate-700">
              {item.description[currentLang]}
            </p>
            <p className="mt-3 text-sm font-semibold text-slate-700">{item.tagline[currentLang]}</p>
            <button
              onClick={() => navigate('/freebord')}
              className="mt-6 rounded-full bg-cyan-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-400"
            >
              {t('home.freebordNewArrivals.cta')}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};



const FeatureLogosSection = () => {
  const { t, i18n } = useTranslation();
  return (
    <section className="bg-white bg-opacity-90 rounded-lg py-8 mb-8" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
      <div className={`max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 ${i18n.language === 'he' ? 'text-right' : 'text-left'}`}>
      {/* Top row: Feature icons */}
      <div className="flex flex-col items-center">
          <img src="/pictures/icon_9board.png" alt={t('home.features.priceAlt')} className="h-20 w-20 mb-2" />
          <div className="text-lg font-bold">{t('home.features.price')}</div>
      </div>
      <div className="flex flex-col items-center">
          <img src="/pictures/testdriveicon.png" alt={t('home.features.testDriveAlt')} className="h-20 w-20 mb-2" />
          <div className="text-lg font-bold">{t('home.features.testDrive')}</div>
      </div>
      <div className="flex flex-col items-center">
          <img src="/pictures/insuranceicon.png" alt={t('home.features.warrantyAlt')} className="h-20 w-20 mb-2" />
          <div className="text-lg font-bold">{t('home.features.warranty')}</div>
        </div>
      </div>
      <div className={`max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 ${i18n.language === 'he' ? 'text-right' : 'text-left'}`}>
      {/* Bottom row: Technical icons */}
      <div className="flex flex-col items-center">
          <img src="/pictures/batteryicon.png" alt={t('home.features.rangeAlt')} className="h-20 w-20 mb-2" />
          <div className="text-lg font-bold">{t('home.features.range')}</div>
      </div>
      <div className="flex flex-col items-center">
          <img src="/pictures/speedometericon.png" alt={t('home.features.speedAlt')} className="h-20 w-20 mb-2" />
          <div className="text-lg font-bold">{t('home.features.speed')}</div>
      </div>
      <div className="flex flex-col items-center">
          <img src="/pictures/icon_wheel.png" alt={t('home.features.wheelsAlt')} className="h-20 w-20 mb-2" />
          <div className="text-lg font-bold">{t('home.features.wheels')}</div>
      </div>
    </div>
  </section>
);
};

const GoogleMapSection = () => {
  const { t, i18n } = useTranslation();
  return (
    <section className="bg-white bg-opacity-90 rounded-lg py-8" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
      <div className={`max-w-3xl mx-auto text-center ${i18n.language === 'he' ? 'text-right' : 'text-left'}`}>
        <h3 className="text-2xl font-bold mb-4">{t('home.map.title')}</h3>
        <div className="mb-4 text-lg font-semibold">{t('home.map.address')}</div>
      <div className="w-full h-72 rounded-lg overflow-hidden shadow-lg">
        <iframe
          title="Blue Board Location"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps?q=ידידיה+פרנקל+50,+תל+אביב&output=embed"
        ></iframe>
      </div>
    </div>
  </section>
);
};

export default HomePage; 
